import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, ExternalLink, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, TOKEN_KEYS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Billing from "@/pages/vendor/Billing";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage",
  "Pacific/Honolulu", "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Paris", "Australia/Sydney", "UTC"];

function Invoices() {
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => { api.get("/billing/invoices").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <Panel data-testid="account-invoices">
      <h2 className="mb-3 text-lg font-semibold">Billing history</h2>
      {rows === null ? <Loader2 className="h-4 w-4 animate-spin" /> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="account-invoices-empty">No invoices yet. They appear here after your first payment.</p>
      ) : (
        <div className="divide-y">
          {rows.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm" data-testid={`account-invoice-${i.id}`}>
              <span className="w-28 text-muted-foreground">{new Date(i.created * 1000).toLocaleDateString()}</span>
              <span className="font-mono font-semibold">${i.amount.toFixed(2)}</span>
              <span className={`rounded-sm px-1.5 py-0.5 text-xs capitalize ${i.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{i.status}</span>
              <span className="flex-1" />
              {i.url && <a href={i.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><ExternalLink className="h-3.5 w-3.5" /> View</a>}
              {i.pdf && <a href={i.pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><Download className="h-3.5 w-3.5" /> PDF</a>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function Settings({ initial, onSaved }: { initial: any; onSaved: () => void }) {
  const [s, setS] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/vendor/account/settings", s); toast.success("Settings saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Panel className="space-y-3" data-testid="account-settings">
      <h2 className="text-lg font-semibold">General settings</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Business name</Label><Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="account-name-input" /></div>
        <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={s.contact_email} onChange={(e) => setS({ ...s, contact_email: e.target.value })} data-testid="account-email-input" /></div>
        <div className="space-y-1.5"><Label>Time zone</Label>
          <Select value={s.timezone} onValueChange={(v) => setS({ ...s, timezone: v })}>
            <SelectTrigger data-testid="account-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-sm border p-3 text-sm sm:mt-6">Email me when a new lead arrives
          <Switch checked={s.notify_new_lead} onCheckedChange={(v) => setS({ ...s, notify_new_lead: v })} data-testid="account-lead-alerts-switch" /></label>
      </div>
      <Button onClick={save} disabled={busy} className="bg-primary text-white hover:bg-primary/90" data-testid="account-settings-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button>
    </Panel>
  );
}

function CloseAccount({ name }: { name: string }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const close = async () => {
    setBusy(true);
    try {
      await api.post("/vendor/account/close", { confirm_name: typed });
      localStorage.removeItem(TOKEN_KEYS.vendor);
      window.location.href = "/?closed=1";
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Panel className="space-y-2 border-rose-200" data-testid="account-close-panel">
      <h2 className="text-lg font-semibold text-rose-700">Close your ExpoEventHub account</h2>
      <p className="text-sm text-muted-foreground">Cancels your subscription right away, signs out your whole team and turns off every booth QR code. Your events, leads and files are permanently deleted after 30 days. Email support before then to restore the account.</p>
      <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setOpen(true)} data-testid="account-close-button">Close account</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-close-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><TriangleAlert className="h-5 w-5 text-rose-600" /> Close {name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Type <b>{name}</b> to confirm.</p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="account-close-confirm-input" />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="account-close-cancel-button">Keep my account</Button>
            <Button disabled={busy || typed.trim().toLowerCase() !== name.trim().toLowerCase()} onClick={close} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="account-close-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Close account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export default function Account() {
  const { vendor, reload } = useAuth();
  const [d, setD] = useState<any>(null);
  const load = () => api.get("/vendor/account").then((r) => setD(r.data));
  const isOwner = vendor.user.staff_role === "owner";
  useEffect(() => { if (isOwner) load(); }, [isOwner]);
  if (!isOwner) return <Navigate to="/dashboard" replace />;
  return (
    <div className="space-y-5">
      <PageHead eyebrow={vendor.vendor.name} title="Account" />
      <section data-testid="account-plan"><h2 className="mb-3 text-lg font-semibold">Plan & payment</h2><Billing embedded /></section>
      <Invoices />
      {d && <Settings initial={d.settings} onSaved={() => { load(); reload("vendor"); }} />}
      <CloseAccount name={vendor.vendor.name} />
    </div>
  );
}
